import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Box, Typography, Button, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Grid, Chip,
  IconButton, Tooltip, Alert, CircularProgress, MenuItem,
  Switch, FormControlLabel
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';

const EMPTY = {
  nombre: '', prefijo: '', moneda: null, cuenta_id: null, lado: 'D',
  porcentaje: 100, descripcion_linea: '', orden: 0, activo: 1, tipo_documento_filtro: []
};

// Tipos de documento disponibles para filtrar (agrupados)
const TIPOS_DOC_GRUPOS = [
  { grupo: 'Manual / Diario', tipos: ['DI','AJ','AP','CI','DP'], color: '#1565c0' },
  { grupo: 'Caja y Bancos',   tipos: ['RE','BP','CH','TF','DE','NA','CB'], color: '#e65100' },
  { grupo: 'ERP',             tipos: ['VE','CO','NC','ND','NV','GU'], color: '#2e7d32' },
];

const MONEDAS = [
  { v: null,  l: 'Todas las monedas (por defecto)' },
  { v: 'PEN', l: 'Soles — PEN' },
  { v: 'USD', l: 'Dólares — USD' },
  { v: 'EUR', l: 'Euros — EUR' },
];

export default function AsientoRapidoConfig() {
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState('');
  const [reglas, setReglas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [cuentasOpc, setCuentasOpc] = useState([]);
  const [cuentaInputVal, setCuentaInputVal] = useState('');
  const [cuentaSeleccionada, setCuentaSeleccionada] = useState(null);

  useEffect(() => {
    axios.get('/api/contabilidad/empresas')
      .then(r => {
        const todas = Array.isArray(r.data) ? r.data : [];
        setEmpresas(todas);
        const activas = todas.filter(e => e.activo);
        if (activas.length === 1) setEmpresaId(String(activas[0].id));
      })
      .catch(() => {});
  }, []);

  const cargar = useCallback(async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const r = await axios.get('/api/contabilidad/reglas-rapido', { params: { empresa_id: empresaId } });
      setReglas(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError('Error al cargar reglas: ' + (e.response?.data?.error || e.message));
    } finally {
      setLoading(false);
    }
  }, [empresaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const buscarCuentas = async (termino) => {
    if (!empresaId) return;
    try {
      const r = await axios.get('/api/contabilidad/plan', {
        params: { empresa_id: empresaId, solo_hojas: '1', buscar: termino || '' }
      });
      setCuentasOpc(Array.isArray(r.data) ? r.data.slice(0, 50) : []);
    } catch (_) {}
  };

  const abrirNueva = () => {
    setEditando(null);
    setForm(EMPTY);
    setCuentaInputVal('');
    setCuentaSeleccionada(null);
    buscarCuentas('');
    setError('');
    setOpen(true);
  };

  const abrirEditar = (r) => {
    setEditando(r);
    setForm({
      nombre: r.nombre,
      prefijo: r.prefijo,
      moneda: r.moneda || null,
      cuenta_id: r.cuenta_id,
      lado: r.lado,
      porcentaje: r.porcentaje,
      descripcion_linea: r.descripcion_linea || '',
      orden: r.orden,
      activo: r.activo ?? 1,
      tipo_documento_filtro: r.tipo_documento_filtro ? r.tipo_documento_filtro.split(',') : []
    });
    const cuentaObj = r.cuenta_id ? { id: r.cuenta_id, codigo: r.cuenta_codigo, descripcion: r.cuenta_descripcion } : null;
    setCuentaSeleccionada(cuentaObj);
    setCuentaInputVal(r.cuenta_codigo ? `${r.cuenta_codigo} - ${r.cuenta_descripcion}` : '');
    buscarCuentas('');
    setError('');
    setOpen(true);
  };

  const guardar = async () => {
    if (!form.nombre || !form.prefijo || !form.cuenta_id) {
      setError('Nombre, prefijo y cuenta son requeridos');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const body = {
        ...form,
        empresa_id: empresaId,
        tipo_documento_filtro: form.tipo_documento_filtro.length > 0
          ? form.tipo_documento_filtro.join(',') : null
      };
      if (editando) {
        await axios.put(`/api/contabilidad/reglas-rapido/${editando.id}`, body);
      } else {
        await axios.post('/api/contabilidad/reglas-rapido', body);
      }
      setSuccess(editando ? 'Regla actualizada' : 'Regla creada');
      setOpen(false);
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const eliminar = async (id) => {
    if (!window.confirm('¿Eliminar esta regla?')) return;
    try {
      await axios.delete(`/api/contabilidad/reglas-rapido/${id}`);
      setSuccess('Regla eliminada');
      cargar();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  // Agrupar por prefijo + moneda
  const reglasPorPrefijo = reglas.reduce((acc, r) => {
    const key = `${r.prefijo}||${r.moneda || '__global__'}`;
    if (!acc[key]) acc[key] = { prefijo: r.prefijo, moneda: r.moneda || null, items: [] };
    acc[key].items.push(r);
    return acc;
  }, {});

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <AutoFixHighIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h5" fontWeight="bold">Asiento Rápido — Configuración</Typography>
        </Box>
        <TextField select label="Empresa" size="small" value={empresaId}
          onChange={e => setEmpresaId(e.target.value)}
          sx={{ minWidth: { xs: '100%', sm: 260 }, ml: { sm: 'auto' } }}>
          {empresas.map(e => (
            <MenuItem key={e.id} value={String(e.id)}>{e.razon_social}</MenuItem>
          ))}
        </TextField>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        Define reglas por <strong>prefijo de cuenta</strong> y <strong>moneda</strong> (opcional). Si el asiento es en dólares y hay regla para USD, usa esa; si no, usa la regla de "Todas las monedas" como predeterminado.
        <br />Ejemplo: prefijo <code>6</code> Todas → genera 421201 (H) en soles. Prefijo <code>6</code> USD → genera 421202 (H) en dólares.
      </Alert>

      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}
      {error && !open && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {!empresaId ? (
        <Alert severity="info">Seleccione una empresa para ver las reglas configuradas.</Alert>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNueva} disabled={!empresaId}>
              Nueva Regla
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
          ) : reglas.length === 0 ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              No hay reglas configuradas. Cree la primera regla para habilitar la auto-sugerencia de asientos.
            </Paper>
          ) : (
            Object.entries(reglasPorPrefijo).map(([key, grupo]) => (
              <Paper key={key} elevation={2} sx={{ mb: 2 }}>
                <Box sx={{
                  px: 2, py: 1, borderRadius: '4px 4px 0 0',
                  bgcolor: grupo.moneda ? 'secondary.dark' : 'primary.dark',
                  display: 'flex', alignItems: 'center', gap: 1,
                }}>
                  <Chip label={`Prefijo: ${grupo.prefijo}*`} size="small"
                    sx={{ bgcolor: 'white', fontWeight: 'bold', fontFamily: 'monospace' }} />
                  {grupo.moneda ? (
                    <Chip label={grupo.moneda} size="small"
                      sx={{ bgcolor: '#FFD700', color: '#333', fontWeight: 'bold', fontSize: 11 }} />
                  ) : (
                    <Chip label="Todas las monedas" size="small"
                      sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontSize: 11 }} />
                  )}
                  <Typography variant="subtitle2" color="white">
                    {grupo.items.length} regla{grupo.items.length !== 1 ? 's' : ''}
                    {grupo.moneda
                      ? ` — exclusivo ${grupo.moneda}`
                      : ' — aplica si no hay regla específica por moneda'}
                  </Typography>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        {['Nombre', 'Cuenta compañera', 'Lado', '% aplicar', 'Glosa auto', 'Aplica en', 'Orden', 'Estado', 'Acciones'].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 'bold', fontSize: 12 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {grupo.items.map(r => (
                        <TableRow key={r.id} hover>
                          <TableCell>{r.nombre}</TableCell>
                          <TableCell>
                            <span style={{ fontFamily: 'monospace', color: '#388e3c', marginRight: 6 }}>{r.cuenta_codigo}</span>
                            <span style={{ fontSize: 12, color: '#555' }}>{r.cuenta_descripcion}</span>
                          </TableCell>
                          <TableCell>
                            <Chip label={r.lado === 'D' ? 'DEBE' : 'HABER'}
                              size="small" color={r.lado === 'D' ? 'primary' : 'warning'} />
                          </TableCell>
                          <TableCell align="right">
                            <Typography fontFamily="monospace">{Number(r.porcentaje).toFixed(2)}%</Typography>
                          </TableCell>
                          <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {r.descripcion_linea || '—'}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 160 }}>
                            {r.tipo_documento_filtro ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                                {r.tipo_documento_filtro.split(',').map(t => {
                                  const grp = TIPOS_DOC_GRUPOS.find(g => g.tipos.includes(t));
                                  return (
                                    <Chip key={t} label={t} size="small"
                                      sx={{ height: 18, fontSize: 10, fontWeight: 'bold',
                                        bgcolor: grp?.color || '#607d8b', color: '#fff' }} />
                                  );
                                })}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.disabled" fontStyle="italic">Todos</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">{r.orden}</TableCell>
                          <TableCell align="center">
                            <Chip
                              label={r.activo ? 'Activa' : 'Inactiva'}
                              size="small"
                              color={r.activo ? 'success' : 'default'}
                              variant={r.activo ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title="Editar">
                              <IconButton size="small" onClick={() => abrirEditar(r)}><EditIcon fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Eliminar">
                              <IconButton size="small" color="error" onClick={() => eliminar(r.id)}><DeleteIcon fontSize="small" /></IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))
          )}
        </>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editando ? 'Editar Regla' : 'Nueva Regla de Asiento Rápido'}</DialogTitle>
        <DialogContent dividers>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField label="Nombre de la regla *" value={form.nombre} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Ej: IGV 18% Compras" />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Prefijo de cuenta *" value={form.prefijo} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, prefijo: e.target.value.replace(/\D/g,'') }))}
                helperText="Ej: 6 → aplica a toda cuenta 6XXXXX"
                inputProps={{ maxLength: 10, style: { fontFamily: 'monospace' } }} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField label="Moneda" select value={form.moneda ?? ''} size="small" fullWidth
                sx={{ minWidth: 200 }}
                SelectProps={{ displayEmpty: true }}
                InputLabelProps={{ shrink: true }}
                onChange={e => setForm(f => ({ ...f, moneda: e.target.value === '' ? null : e.target.value }))}
                helperText="Deja en 'Todas' para que sea el predeterminado si no hay regla por moneda">
                {MONEDAS.map(m => (
                  <MenuItem key={m.v ?? '__null__'} value={m.v ?? ''}>{m.l}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="Lado *" name="lado" select value={form.lado} size="small" fullWidth
                sx={{ minWidth: 170 }}
                onChange={e => setForm(f => ({ ...f, lado: e.target.value }))}>
                <MenuItem value="D">DEBE</MenuItem>
                <MenuItem value="H">HABER</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField label="% a aplicar *" type="number" value={form.porcentaje} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, porcentaje: e.target.value }))}
                inputProps={{ min: 0, max: 999, step: 0.01 }}
                helperText="118 = IGV incluido, 18 = solo IGV" />
            </Grid>
            <Grid item xs={12} sm={4} />
            <Grid item xs={12}>
              <Autocomplete
                options={
                  cuentaSeleccionada && !cuentasOpc.find(c => c.id === cuentaSeleccionada.id)
                    ? [cuentaSeleccionada, ...cuentasOpc]
                    : cuentasOpc
                }
                getOptionLabel={o => `${o.codigo} - ${o.descripcion}`}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={cuentaSeleccionada}
                inputValue={cuentaInputVal}
                onInputChange={(_, val, reason) => {
                  setCuentaInputVal(val);
                  if (reason === 'input') buscarCuentas(val);
                }}
                onChange={(_, val) => {
                  setCuentaSeleccionada(val);
                  setForm(f => ({ ...f, cuenta_id: val?.id || null }));
                }}
                renderInput={params => (
                  <TextField {...params} label="Cuenta compañera *" size="small"
                    helperText="Cuenta que se generará automáticamente en la línea compañera" />
                )}
                renderOption={(props, o) => (
                  <li {...props} key={o.id}>
                    <span style={{ fontFamily: 'monospace', color: '#388e3c', marginRight: 8 }}>{o.codigo}</span>
                    {o.descripcion}
                  </li>
                )}
                noOptionsText="Sin resultados"
                size="small"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label="Glosa automática (opcional)" value={form.descripcion_linea} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, descripcion_linea: e.target.value }))}
                inputProps={{ maxLength: 100 }}
                placeholder="Ej: IGV 18%" />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField label="Orden" type="number" value={form.orden} size="small" fullWidth
                onChange={e => setForm(f => ({ ...f, orden: parseInt(e.target.value) || 0 }))}
                inputProps={{ min: 0 }} />
            </Grid>
            <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.activo}
                    onChange={e => setForm(f => ({ ...f, activo: e.target.checked ? 1 : 0 }))}
                    color="success"
                  />
                }
                label={<Typography variant="body2" color={form.activo ? 'success.main' : 'text.disabled'}>
                  {form.activo ? 'Activa' : 'Inactiva'}
                </Typography>}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Aplica solo en estos tipos de documento (deja vacío = aplica en todos)
              </Typography>
              {TIPOS_DOC_GRUPOS.map(grp => (
                <Box key={grp.grupo} sx={{ mb: 0.8 }}>
                  <Typography variant="caption" sx={{ color: grp.color, fontWeight: 'bold', mr: 1 }}>
                    {grp.grupo}:
                  </Typography>
                  {grp.tipos.map(t => {
                    const sel = form.tipo_documento_filtro.includes(t);
                    return (
                      <Chip
                        key={t}
                        label={t}
                        size="small"
                        onClick={() => setForm(f => ({
                          ...f,
                          tipo_documento_filtro: sel
                            ? f.tipo_documento_filtro.filter(x => x !== t)
                            : [...f.tipo_documento_filtro, t]
                        }))}
                        sx={{
                          mr: 0.5, mb: 0.3, height: 22, fontSize: 11, fontWeight: 'bold',
                          cursor: 'pointer',
                          bgcolor: sel ? grp.color : 'transparent',
                          color: sel ? '#fff' : grp.color,
                          border: `1.5px solid ${grp.color}`,
                          '&:hover': { bgcolor: sel ? grp.color : `${grp.color}22` }
                        }}
                      />
                    );
                  })}
                </Box>
              ))}
              {form.tipo_documento_filtro.length > 0 && (
                <Button size="small" onClick={() => setForm(f => ({ ...f, tipo_documento_filtro: [] }))}
                  sx={{ mt: 0.5, fontSize: 11 }}>
                  Limpiar selección (aplicar en todos)
                </Button>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={saving}>Cancelar</Button>
          <Button onClick={guardar} variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={20} /> : editando ? 'Guardar' : 'Crear regla'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
